import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, delay, exhaustMap, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { AdminPanelService } from '@app/services/admin-panel.service';
import { CacheService } from '@app/services/cache.service';
import { CompaniesService } from '@app/services/companies.service';
import { ContractorsService } from '@app/services/contractors.service';
import { UserService } from '@app/services/user.service';
import {
  assignUserToProperty,
  saveUserToGeneralRole,
} from '@main-application/administration/store/actions/administration.actions';
import { UserToGeneralRoleModel } from '@shared/interfaces/user-to-general-role-model';
import { UserToPropertyMapping } from '@shared/interfaces/user-to-property-mapping';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

import {
  addAddressToCompany,
  addAddressToCompanyFailed,
  addAddressToCompanySuccess,
  addCompany,
  addCompanyFailed,
  addCompanySuccess,
  addCompanyWithOwner,
  addCompanyWithOwnerFailure,
  addCompanyWithOwnerFailureUserExist,
  addCompanyWithOwnerSuccess,
  addEmployeeToCompany,
  addEmployeeToCompanyFailed,
  addEmployeeToCompanySuccess,
  deleteCompany,
  deleteCompanyFailed,
  deleteCompanySuccess,
  deleteEmployeeFromCompany,
  deleteEmployeeFromCompanyFailed,
  deleteEmployeeFromCompanySuccess,
  getCompaniesList,
  getCompaniesListFailed,
  getCompaniesListSuccess,
  getCompany,
  getCompanyFailed,
  getCompanySuccess,
  getContractorList,
  getContractorListFailed,
  getContractorListSuccess,
  updateAddressInCompany,
  updateAddressInCompanyFailed,
  updateAddressInCompanySuccess,
  updateCompany,
  updateCompanyFailed,
  updateCompanySuccess,
} from './companies.actions';

@Injectable()
export class CompaniesEffects {
  constructor(
    private actions$: Actions,
    private snackbarService: SnackbarService,
    private cacheService: CacheService,
    private userService: UserService,
    private adminService: AdminPanelService,
    private companiesService: CompaniesService,
    private contractorsService: ContractorsService,
    private store: Store<{}>
  ) {}

  getCompaniesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCompaniesList),
      exhaustMap(action =>
        this.companiesService.getCompanyList().pipe(
          map(list => getCompaniesListSuccess({ companyList: list || [] })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingCompanyList);
            return of(getCompaniesListFailed());
          })
        )
      )
    )
  );

  getCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCompany),
      switchMap(action =>
        this.companiesService.getCompany(action.companyId).pipe(
          map(companyModel => getCompanySuccess({ companyModel })),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingCompanyData);
            return of(getCompanyFailed());
          })
        )
      )
    )
  );

  addCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCompany),
      mergeMap(action =>
        this.companiesService.addNewCompany(action.companyModel).pipe(
          tap(companyModel => {
            this.snackbarService.success(companyModel.displayName + ' added');
            this.cacheService.resetCacheByKey('companiesList');
          }),
          mergeMap(companyModel => [getCompaniesList(), addCompanySuccess({ companyModel })]),
          catchError(err => {
            this.snackbarService.error(err?.error || SnackbarErrorMessage.AddingNewVendor);
            return of(addCompanyFailed());
          })
        )
      )
    )
  );

  addCompanyWithOwner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCompanyWithOwner),
      switchMap(action =>
        this.userService.inviteUser(action.user).pipe(
          map(user => {
            const userId = user.id;
            const roleId = action.roleId;
            const usersToGeneralRoleList: UserToGeneralRoleModel[] = [{ roleId, userId }];
            const userToPropertyRoleList: UserToPropertyMapping[] = action.selectedProperty.map<UserToPropertyMapping>(
              propertyId => {
                return {
                  propertyId,
                  userId,
                  roleId,
                  id: 0,
                };
              }
            );
            return { user, usersToGeneralRoleList, userToPropertyRoleList };
          }),
          mergeMap(({ user, usersToGeneralRoleList, userToPropertyRoleList }) => {
            return this.companiesService.addNewCompany({ ...action.companyModel, contactPersonId: user.id }).pipe(
              mergeMap(company =>
                this.companiesService.addEmployeeToCompany({ companyId: company.id, userIds: [user.id] }).pipe(
                  delay(800),
                  map(() => {
                    this.store.dispatch(saveUserToGeneralRole({ usersToGeneralRoleList: usersToGeneralRoleList }));
                    this.store.dispatch(assignUserToProperty({ userToPropertyRoleList: userToPropertyRoleList }));
                    return company;
                  })
                )
              )
            );
          }),
          map(company => addCompanyWithOwnerSuccess({ company })),
          tap(() => this.cacheService.resetCacheByKey('companiesList')),
          catchError(error => {
            const isUserExistError = error.error === 'User exists in Azure Active Directory';

            if (!isUserExistError) this.snackbarService.error(error?.error || SnackbarErrorMessage.AddingNewVendor);
            return of(
              addCompanyWithOwnerFailure(),
              isUserExistError
                ? addCompanyWithOwnerFailureUserExist({
                    existedUserEmail: action.user.email,
                    phoneNumberAddedAsIdentity: action.user?.phoneNumberAddedAsIdentity,
                    phoneNumber: action.user?.phoneNumber,
                  })
                : null
            );
          })
        )
      ),
      catchError(err => {
        this.snackbarService.error(err?.error || SnackbarErrorMessage.AddingNewVendor);
        return of(addCompanyWithOwnerFailure());
      })
    )
  );

  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCompany),
      mergeMap(action =>
        this.companiesService.updateCompany(action.companyModel).pipe(
          mergeMap(companyModel => [updateCompanySuccess({ companyModel })]),
          tap(() => this.cacheService.resetCacheByKey('companiesList')),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingVendor);
            return of(updateCompanyFailed());
          })
        )
      )
    )
  );

  deleteCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteCompany),
      mergeMap(action =>
        this.companiesService.deleteCompany(action.company.id).pipe(
          tap(() => {
            this.snackbarService.success(action.company.displayName + ' deleted');
            this.cacheService.resetCacheByKey('companiesList');
          }),
          mergeMap(() => [deleteCompanySuccess(), getCompaniesList()]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingVendor);
            return of(deleteCompanyFailed());
          })
        )
      )
    )
  );

  addEmployeeToCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addEmployeeToCompany),
      mergeMap(action =>
        this.companiesService.addEmployeeToCompany(action.updateEmployeeItem).pipe(
          mergeMap(companyList => [
            addEmployeeToCompanySuccess(),
            getCompany({ companyId: action.updateEmployeeItem.companyId }),
            getContractorList(),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.AddingEmployee);
            return of(addEmployeeToCompanyFailed());
          })
        )
      )
    )
  );

  deleteEmployeeFromCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteEmployeeFromCompany),
      mergeMap(action =>
        this.companiesService.deleteEmployeeFromCompany(action.updateEmployeeItem).pipe(
          mergeMap(companyList => [
            deleteEmployeeFromCompanySuccess(),
            getCompany({ companyId: action.updateEmployeeItem.companyId }),
            getContractorList(),
          ]),
          catchError(() => {
            this.snackbarService.error(SnackbarErrorMessage.DeletingEmployee);
            return of(deleteEmployeeFromCompanyFailed());
          })
        )
      )
    )
  );

  getContractorList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getContractorList),
      exhaustMap(() => {
        return this.contractorsService.getUnassignedContractorsList().pipe(
          map(contractors => getContractorListSuccess({ contractors })),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.RetrievingVendors);
            return of(getContractorListFailed());
          })
        );
      })
    )
  );

  addAddressToCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addAddressToCompany),
      mergeMap(action => {
        return this.companiesService.addAddressToCompany(action.postCompanyAddressModel).pipe(
          mergeMap(contractors => [
            addAddressToCompanySuccess(),
            getCompany({ companyId: action.postCompanyAddressModel.companyId }),
          ]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.AddingAddress);
            return of(addAddressToCompanyFailed());
          })
        );
      })
    )
  );

  updateAddressInCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateAddressInCompany),
      mergeMap(action => {
        return this.companiesService.updateAddressInCompany(action.restAddressModel, action.companyId).pipe(
          mergeMap(contractors => [updateAddressInCompanySuccess(), getCompany({ companyId: action.companyId })]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.UpdatingAddress);
            return of(updateAddressInCompanyFailed());
          })
        );
      })
    )
  );
}
